import { useEffect, useRef, useCallback } from 'preact/hooks';

/**
 * 返回当前最新值的 Hook，可以避免闭包问题
 * @param value
 * @returns {React.MutableRefObject<*>}
 */
export function useLatest(value) {
  const ref = useRef(value);
  ref.current = value;

  return ref;
}

/**
 * 一个可以处理 setInterval 的 Hook
 * @param fn
 * @param delay  间隔时间，当取值 null 时会停止计时器
 * @param options immediate 是否在首次渲染时立即执行 默认false
 * @returns fn 清除定时器
 */
export function useInterval(fn, delay, options) {
  const immediate = options?.immediate;
  const fnRef = useLatest(fn);
  const timerRef = useRef();

  useEffect(() => {
    if (typeof delay !== 'number' || delay < 0) return;
    if (immediate) {
      fnRef.current();
    }
    timerRef.current = setInterval(() => {
      fnRef.current();
    }, delay);
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [delay]);

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  }, []);
  return clear;
}

/**
 * 自定义 Hook：用于实现基于时间间隔的请求轮询
 * @param {Function} fn - 需要执行的请求函数（支持异步）
 * @param {number | null} delay - 请求之间的时间间隔（毫秒），如果为假，则停止轮询
 * @param {Object} options - 配置项
 * @param {boolean} options.immediate - 是否立即执行第一次请求，默认为 false
 * @param {number} options.maxDelay - 最大轮询间隔（毫秒），用于指数退避，默认不限制
 * @param {number} options.backoffFactor - 退避因子，默认 1（不退避）
 * @returns {Function} - 返回一个清理函数，用于手动停止轮询
 */
export const useRequestInterval = (fn, delay, options = { immediate: false }) => {
  const fnRef = useLatest(fn);
  const intervalId = useRef(); // 保存定时器 ID
  const currentDelay = useRef(delay); // 当前实际 delay（用于退避）

  // 递归执行请求
  const executeRequest = async () => {
    if (typeof delay !== 'number' || delay < 0) return;
    await fnRef.current(); // 执行请求
    // 如果配置了退避因子，逐步增加间隔
    if (options.backoffFactor && options.backoffFactor > 1) {
      currentDelay.current = Math.min(currentDelay.current * options.backoffFactor, options.maxDelay || delay * 10);
    }
    intervalId.current = setTimeout(executeRequest, currentDelay.current); // 请求完成后启动下一次请求
  };

  // 启动或停止定时器
  useEffect(() => {
    currentDelay.current = delay; // 重置 delay
    if (delay && options.immediate) {
      executeRequest(); // 立即执行第一次请求
    }
    return () => {
      if (intervalId.current) {
        clearTimeout(intervalId.current); // 清理定时器
      }
    };
  }, [delay, options.immediate]);

  // 返回清理函数
  return () => {
    if (intervalId.current) {
      clearTimeout(intervalId.current); // 清理定时器
    }
  };
};
